import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { ThirdwebProvider } from "@thirdweb-dev/react";
import "./styles/globals.css";
import { Chain } from "@thirdweb-dev/chains";
import { Toaster } from "./components/ui/Toaster";
import { getGasless } from "./utils/getGasless";
import {
  biconomyApiIdConst,
  biconomyApiKeyConst,
  chainConst,
  relayerUrlConst,
  clientIdConst,
} from "./consts/parameters";

// Define container and root
const container = document.getElementById("root");
if (!container) throw new Error("Root element not found");
const root = createRoot(container);

// Parse URL parameters for dynamic configuration
const urlParams = new URL(window.location.toString()).searchParams;

const relayerUrl = urlParams.get("relayUrl") || relayerUrlConst || "";
const biconomyApiKey = urlParams.get("biconomyApiKey") || biconomyApiKeyConst || "";
const biconomyApiId = urlParams.get("biconomyApiId") || biconomyApiIdConst || "";
const clientId = urlParams.get("clientId") || clientIdConst || "";

// Gasless configuration (commented out for testing)
// const sdkOptions = getGasless(relayerUrl, biconomyApiKey, biconomyApiId);

// Chain configurations
const baseMainnet: Chain = {
  chainId: 8453, // Base Mainnet Chain ID
  rpc: ["https://base-mainnet.g.alchemy.com/v2/h7aYXpbxMcz2MW9DwUpW3Qxa2CcafDSJ"],
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  shortName: "base",
  slug: "base-mainnet",
  name: "Base Mainnet",
  testnet: false,
  chain: "Base Mainnet",
};

const baseSepolia: Chain = {
  chainId: 84532, // Base Sepolia Testnet Chain ID
  rpc: ["https://base-sepolia.g.alchemy.com/v2/h7aYXpbxMcz2MW9DwUpW3Qxa2CcafDSJ"],
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  shortName: "base-sepolia",
  slug: "base-sepolia",
  name: "Base Sepolia",
  testnet: true,
  chain: "Base Sepolia",
};

const pulseChain: Chain = {
  chainId: 369, // PulseChain Mainnet Chain ID
  rpc: ["https://site1.moralis-nodes.com/pulse/4ca08700b5ea44f18a91582f0b620c03"],
  nativeCurrency: {
    name: "Pulse",
    symbol: "PLS",
    decimals: 18,
  },
  shortName: "pulsechain",
  slug: "pulsechain",
  name: "PulseChain",
  testnet: false,
  chain: "PulseChain",
};

const pulseChainTestnet: Chain = {
  chainId: 943, // PulseChain Testnet Chain ID
  rpc: ["https://rpc.v4.testnet.pulsechain.com"],
  nativeCurrency: {
    name: "Test Pulse",
    symbol: "tPLS",
    decimals: 18,
  },
  shortName: "pulsechain-testnet",
  slug: "pulsechain-testnet",
  name: "PulseChain Testnet",
  testnet: true,
  chain: "PulseChain Testnet",
};

// Supported chains for ThirdwebProvider
const supportedChains: Chain[] = [baseMainnet, baseSepolia, pulseChain, pulseChainTestnet];

// Render the app
root.render(
  <React.StrictMode>
    <ThirdwebProvider
      activeChain={baseMainnet} // Default active chain
      supportedChains={supportedChains} // List of supported chains
      // sdkOptions={sdkOptions} // Gasless configuration disabled for testing
      clientId={clientId} // Thirdweb client ID
    >
      <Toaster />
      <App />
    </ThirdwebProvider>
  </React.StrictMode>
);

// Debugging: Log Thirdweb configuration
console.log("Thirdweb Configuration:", {
  activeChain: baseMainnet.chainId,
  supportedChains: supportedChains.map((chain) => chain.chainId),
  sdkOptions: "Disabled for testing", // Updated to reflect the change
  clientId,
});